import React from 'react'
import Helmet from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'

// caly_googleMarkup: Globales Organization/LocalBusiness/Service Schema (Calysto Marketing Solutions)
const caly_googleMarkup = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://www.extendit.at/#organization',
      'name': 'extendIT GmbH',
      'url': 'https://www.extendit.at/',
      'logo': 'https://www.extendit.at/icons/icon-512x512.png?v=eaa43dfe207317e1f4aec30f741428c1',
      'sameAs': [
        'https://www.facebook.com/extendIT/',
        'https://www.instagram.com/explore/locations/503038679727908/extendit-gmbh/',
        'https://twitter.com/extendit_gmbh',
      ],
    },
    {
      '@type': 'LocalBusiness',
      '@id': 'https://www.extendit.at/#localbusiness',
      'name': 'extendIT GmbH',
      'image': 'https://www.extendit.at/icons/icon-512x512.png?v=eaa43dfe207317e1f4aec30f741428c1',
      'url': 'https://www.extendit.at/',
      'telephone': '+43 1 8027675',
      'priceRange': '€€',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Schottenfeldgasse 78/3/1-3',
        'addressLocality': 'Wien',
        'postalCode': '1070',
        'addressCountry': 'AT',
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': 48.2000,
        'longitude': 16.3500,
      },
      'openingHoursSpecification': [
        {
          '@type': 'OpeningHoursSpecification',
          'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          'opens': '09:00',
          'closes': '17:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          'dayOfWeek': ['Saturday', 'Sunday'],
          'opens': '00:00',
          'closes': '00:00',
        },
      ],
      'sameAs': ['https://maps.app.goo.gl/7AhTSVGueusZto6p6'],
      'areaServed': { '@type': 'Place', 'name': 'Wien und Umgebung' },
      'parentOrganization': { '@id': 'https://www.extendit.at/#organization' },
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-it-support',
      'name': 'IT-Support für Mac & Windows',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'areaServed': { '@type': 'Place', 'name': 'Wien' },
      'serviceType': 'IT Support',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-it-infrastruktur',
      'name': 'IT-Infrastruktur',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'IT Infrastruktur',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-datensicherheit',
      'name': 'Datensicherheit',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'IT Security',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-dam',
      'name': 'Digital Asset Management NetX',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'Digital Asset Management',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-cloud',
      'name': 'Cloud-Consulting & Begleitung',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'Cloud Services',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-mac',
      'name': 'Mac Support',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'Apple IT Support',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-client-management',
      'name': 'Client Management Systeme',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'Client Management',
    },
    {
      '@type': 'Service',
      '@id': 'https://www.extendit.at/#service-remote',
      'name': 'extendIT Remote Service',
      'provider': { '@id': 'https://www.extendit.at/#localbusiness' },
      'serviceType': 'Remote IT Support',
    },
  ],
}

const SEO = ({ title, description, image }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            image
            socialImage
            siteUrl
          }
        }
      }
    `
  )

  const defaultImage = site.siteMetadata.siteUrl + site.siteMetadata.socialImage
  const metaDescription = description || site.siteMetadata.description
  const metaImage = image || defaultImage

  return (
    <Helmet
      htmlAttributes={{
        lang: `de`,
      }}
      title={title}
      defaultTitle={site.siteMetadata.title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
    >
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {/* General tags */}
      <meta name="image" content={image} />
      <meta name="description" content={metaDescription} />

      {/* OpenGraph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:description" content={metaDescription} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="ahrefs-site-verification" content="500fd8a4d943aee45d50b9c40b855037b3d2de14824a598280d02f649005216b" />
      <meta name="facebook-domain-verification" content="nnr0edzt71dv048e167mvg7ipajkku" />

      {/* caly_googleMarkup: Globales Organization/LocalBusiness/Service Schema */}
      <script type="application/ld+json">
        {JSON.stringify(caly_googleMarkup)}
      </script>
    </Helmet>
  )
}

export default SEO